import type { Color } from "react-aria-components";

import { useEffect, useMemo, useRef, useState } from "react";
import {
	ColorPicker as AriaColorPicker,
	parseColor,
} from "react-aria-components";

import type {
	ColorPickerActions,
	ColorPickerProviderProps,
	ColorPickerState,
} from "./color-picker-types";

import { ColorPickerContext } from "./color-picker-context";

const parseToHsb = (input: Color | string): Color => {
	const parsed = typeof input === "string" ? parseColor(input) : input;
	return parsed.toFormat("hsb");
};

/** Color state provider. Wraps children in both context and AriaColorPicker. */
export const ColorPickerProvider = ({
	children,
	defaultColorFormat = "hex",
	defaultValue = "#0BA5EC",
	onChange,
	value,
}: ColorPickerProviderProps) => {
	const isControlled = value !== undefined;

	const [internalState, setInternalState] = useState<ColorPickerState>(() => ({
		color: parseToHsb(value ?? defaultValue),
		colorFormat: defaultColorFormat,
	}));

	const state = useMemo((): ColorPickerState => {
		if (isControlled) {
			return {
				color: parseToHsb(value),
				colorFormat: internalState.colorFormat,
			};
		}
		return internalState;
	}, [internalState, isControlled, value]);

	const actions = useMemo(
		(): ColorPickerActions => ({
			setColor: (color) => {
				const next = color.toFormat("hsb");
				if (isControlled) {
					onChange?.(next);
					return;
				}
				setInternalState((current) => ({ ...current, color: next }));
			},
			setColorFormat: (format) =>
				setInternalState((current) => ({ ...current, colorFormat: format })),
		}),
		[isControlled, onChange],
	);

	const prevUncontrolledColor = useRef<Color | null>(null);
	useEffect(() => {
		if (isControlled) return;

		if (
			prevUncontrolledColor.current !== null &&
			internalState.color !== prevUncontrolledColor.current
		) {
			onChange?.(internalState.color);
		}
		prevUncontrolledColor.current = internalState.color;
	}, [internalState.color, isControlled, onChange]);

	return (
		<ColorPickerContext.Provider value={{ actions, state }}>
			<AriaColorPicker
				onChange={(c) => actions.setColor(c)}
				value={state.color}
			>
				{children}
			</AriaColorPicker>
		</ColorPickerContext.Provider>
	);
};
